import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { supabase } from '../lib/supabase';
import { useAuthLinkSession } from '../hooks/useAuthLinkSession';
import { Button } from '../components/ui/Button';
import { Lock, Shield, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

type ResetPasswordFormData = {
  password: string;
  confirmPassword: string;
};

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // An invitation or a recovery link (`?token_hash=`), or « Mot de passe oublié » (useAuthLinkSession).
  const link = useAuthLinkSession();
  const sessionReady = link.status === 'ready';
  const invitation = link.kind === 'invite';
  
  const { register, handleSubmit, watch, formState: { errors } } = useForm<ResetPasswordFormData>();
  const password = watch('password');

  const onSubmit = async (data: ResetPasswordFormData) => {
    setLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password,
      });
      
      if (error) {
        setError(error.message);
      } else {
        setSuccess(true);
        // Rediriger vers la page de connexion après 3 secondes
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err: unknown) {
      setError((err instanceof Error && err.message) || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  // Indicateur de force du mot de passe
  const getPasswordStrength = (pwd: string): { label: string; color: string; width: string } => {
    if (!pwd) return { label: '', color: '', width: '0%' };
    
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^a-zA-Z\d]/.test(pwd)) score++;
    
    if (score <= 1) return { label: 'Faible', color: 'bg-red-500', width: '20%' };
    if (score === 2) return { label: 'Moyen', color: 'bg-orange-500', width: '40%' };
    if (score === 3) return { label: 'Bon', color: 'bg-amber-500', width: '60%' };
    if (score === 4) return { label: 'Fort', color: 'bg-emerald-500', width: '80%' };
    return { label: 'Très fort', color: 'bg-emerald-600', width: '100%' };
  };

  const strength = getPasswordStrength(password || '');

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:block relative w-1/2">
        <img
          className="absolute inset-0 h-full w-full object-cover"
          src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80"
          alt="Admin Dashboard"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/90 to-neutral-900/50 mix-blend-multiply" />
        <div className="absolute inset-0 flex flex-col justify-center px-12">
          <div className="flex items-center space-x-3 mb-6">
            <div className="h-12 w-12 rounded-lg bg-[#0066FF] flex items-center justify-center">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">
              Administration
            </h1>
          </div>
          <p className="text-xl text-neutral-200 mb-8">
            Définissez votre nouveau mot de passe
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="h-12 w-12 rounded-lg bg-[#0066FF] flex items-center justify-center">
                <Lock className="h-6 w-6 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-neutral-900">
              {invitation ? 'Bienvenue' : 'Nouveau mot de passe'}
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              {invitation
                ? 'Vous avez été invité : choisissez le mot de passe de votre compte'
                : 'Choisissez un mot de passe sécurisé pour votre compte'}
            </p>
          </div>

          {success ? (
            <div className="mt-8">
              <div className="flex items-start p-4 border border-emerald-100 rounded-lg bg-emerald-50">
                <CheckCircle className="h-5 w-5 text-emerald-500 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-emerald-800">Mot de passe mis à jour</h3>
                  <p className="mt-1 text-sm text-emerald-700">
                    Votre mot de passe a été modifié avec succès. Vous allez être redirigé vers la page de connexion...
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {(error || link.status === 'invalid') && (
                <div className="mt-6 flex items-center p-4 border border-red-100 rounded-lg bg-red-50">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
                  <p className="text-sm text-red-700">
                    {error ?? 'Lien invalide, expiré ou déjà utilisé. Demandez un nouveau lien à votre administrateur.'}
                  </p>
                </div>
              )}

              {link.status === 'checking' ? (
                <div className="mt-8 flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#0066FF]"></div>
                </div>
              ) : (
                <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-neutral-700">
                      Nouveau mot de passe
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-neutral-400" />
                      </div>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        className={`pl-10 pr-10 block w-full rounded-lg border ${
                          errors.password ? 'border-red-300' : 'border-neutral-300'
                        } px-3 py-2 text-neutral-900 placeholder-neutral-500 focus:border-[#0066FF] focus:ring-[#0066FF]/20 sm:text-sm`}
                        placeholder="••••••••"
                        {...register('password', {
                          required: 'Le mot de passe est requis',
                          minLength: {
                            value: 8,
                            message: 'Le mot de passe doit contenir au moins 8 caractères',
                          },
                        })}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
                        ) : (
                          <Eye className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
                        )}
                      </button>
                      {errors.password && (
                        <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                      )}
                    </div>
                    
                    {/* Indicateur de force */}
                    {password && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-neutral-500">Force du mot de passe</span>
                          <span className="text-xs font-medium text-neutral-700">{strength.label}</span>
                        </div>
                        <div className="w-full bg-neutral-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all duration-300 ${strength.color}`}
                            style={{ width: strength.width }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700">
                      Confirmer le mot de passe
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-neutral-400" />
                      </div>
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        className={`pl-10 pr-10 block w-full rounded-lg border ${
                          errors.confirmPassword ? 'border-red-300' : 'border-neutral-300'
                        } px-3 py-2 text-neutral-900 placeholder-neutral-500 focus:border-[#0066FF] focus:ring-[#0066FF]/20 sm:text-sm`}
                        placeholder="••••••••"
                        {...register('confirmPassword', {
                          required: 'Veuillez confirmer le mot de passe',
                          validate: value =>
                            value === password || 'Les mots de passe ne correspondent pas',
                        })}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
                        ) : (
                          <Eye className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
                        )}
                      </button>
                      {errors.confirmPassword && (
                        <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <p className="text-xs text-[#0066FF]">
                      Votre mot de passe doit contenir au moins 8 caractères. Pour plus de sécurité, utilisez une combinaison de majuscules, minuscules, chiffres et caractères spéciaux.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    isLoading={loading}
                    disabled={!sessionReady}
                    leftIcon={<Lock className="h-5 w-5" />}
                  >
                    {invitation ? 'Créer mon mot de passe' : 'Réinitialiser le mot de passe'}
                  </Button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
